import { useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, ChevronLeft, Sun, Moon } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { useTheme } from '@context/ThemeContext';
import Logo from '@components/ui/Logo';
import { AuthGradientBackground } from '@components/auth/AuthGradientBackground';
import { AuthWelcomeAside } from '@components/auth/AuthWelcomeAside';
import { GoogleSignInButton } from '@components/auth/GoogleSignInButton';
import {
  authDividerLabel,
  authDividerLine,
  authFooterMuted,
  authIconButton,
  authInput,
  authLegalMuted,
  authPageSubtitle,
  authPageTitle,
  authTextLink,
  authTextLinkSemibold,
} from '@components/auth/authFormClasses';
import toast from 'react-hot-toast';
import { trackSignUp } from '@utils/analytics';
import { resolveAuthChromeBack } from '@utils/authEntryPaths';

const RegisterPage = () => {
  const [form, setForm] = useState({ email: '', password: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { register, googleLoginWithCode } = useAuth();
  const { dark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const welcomeState = location.state;
  const fromLocation = location.state?.from;
  const fromPath = fromLocation?.pathname || '/feed';
  const from = `${fromPath}${fromLocation?.search || ''}${fromLocation?.hash || ''}`;

  const handleAuthChromeBack = useCallback(() => {
    const r = resolveAuthChromeBack(location.state);
    if (r.mode === 'to') {
      navigate(r.to, { state: r.state });
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/');
  }, [location.state, navigate]);

  const handleGoogleSuccess = useCallback(
    async (code) => {
      setGoogleLoading(true);
      try {
        const data = await googleLoginWithCode(code);
        trackSignUp('google');
        toast.success('Welcome to Yaarvi!');
        if (!data.user.onboardingComplete) {
          navigate('/onboarding', { replace: true, state: location.state });
        } else {
          navigate(from, { replace: true });
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Google sign-up failed');
      } finally {
        setGoogleLoading(false);
      }
    },
    [googleLoginWithCode, navigate, location.state, from],
  );

  const handleGoogleError = useCallback((err) => {
    if (err?.type === 'popup_closed') return;
    toast.error('Google sign-up failed');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await register('User', form.email, form.password, []);
      trackSignUp('email');
      toast.success('Account created!');
      navigate('/onboarding', { replace: true, state: location.state });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const formCard = (
    <div className="w-full min-w-0 max-w-md">
      <div className="mb-6 text-center md:text-left">
        <h2 className={authPageTitle}>Create your account</h2>
        <p className={authPageSubtitle}>Join the community and start discovering vibes near you.</p>
      </div>

      <div className="mb-6 flex items-center justify-center gap-2 md:justify-start">
        <div className="flex items-center gap-1.5">
          <div className="h-1.5 w-10 rounded-full bg-brand-500 sm:w-12" />
          <div className="h-1.5 w-10 rounded-full bg-surface-200 dark:bg-surface-700 sm:w-12" />
          <div className="h-1.5 w-10 rounded-full bg-surface-200 dark:bg-surface-700 sm:w-12" />
        </div>
        <span className="text-xs text-surface-400 dark:text-surface-500">Step 1 of 3</span>
      </div>

      <GoogleSignInButton
        onSuccess={handleGoogleSuccess}
        onError={handleGoogleError}
        loading={googleLoading}
        disabled={loading}
        label="Continue with Google"
      />

      <div className="my-6 flex items-center gap-3">
        <div className={authDividerLine} />
        <span className={authDividerLabel}>Sign up with</span>
        <div className={authDividerLine} />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className={authInput}
          placeholder="Enter email"
          required
          autoComplete="email"
        />

        <div className="relative">
          <input
            type={showPw ? 'text' : 'password'}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className={`${authInput} pr-12`}
            placeholder="Enter password"
            required
            minLength={6}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowPw(!showPw)}
            className={`absolute right-3.5 top-1/2 -translate-y-1/2 ${authIconButton}`}
            aria-label={showPw ? 'Hide password' : 'Show password'}
          >
            {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <div className="relative">
          <input
            type={showConfirmPw ? 'text' : 'password'}
            value={form.confirmPassword}
            onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
            className={`${authInput} pr-12`}
            placeholder="Confirm password"
            required
            minLength={6}
            autoComplete="new-password"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPw(!showConfirmPw)}
            className={`absolute right-3.5 top-1/2 -translate-y-1/2 ${authIconButton}`}
            aria-label={showConfirmPw ? 'Hide password' : 'Show password'}
          >
            {showConfirmPw ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-sky-500 via-brand-500 to-violet-600 py-3.5 text-sm font-bold text-white shadow-lg shadow-brand-500/25 transition-all duration-300 hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              Creating account...
            </span>
          ) : (
            <>
              Get started
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </form>

      <p className={`mt-6 text-center ${authFooterMuted}`}>
        Already have an account?{' '}
        <Link to="/login" state={welcomeState} className={authTextLink}>
          Log in
        </Link>
      </p>

      <p className={`mt-4 ${authLegalMuted}`}>
        By continuing, you agree to our{' '}
        <Link to="/terms" className={authTextLinkSemibold}>
          Terms
        </Link>{' '}
        &{' '}
        <Link to="/privacy" className={authTextLinkSemibold}>
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );

  return (
    <div className="flex min-h-screen min-w-0 flex-col bg-white dark:bg-surface-950 md:flex-row md:bg-surface-100 md:dark:bg-surface-950">
      <AuthWelcomeAside page="register" />

      <div className="relative flex min-h-[28vh] shrink-0 flex-col md:hidden">
        <AuthGradientBackground className="min-h-[28vh] flex-1">
          <div className="relative z-10 flex items-center justify-between px-4 pt-[max(0.5rem,env(safe-area-inset-top))]">
            <button
              type="button"
              onClick={handleAuthChromeBack}
              className="inline-flex items-center gap-0.5 text-sm font-semibold text-white/95 transition-colors hover:text-white"
            >
              <ChevronLeft size={18} strokeWidth={2.5} />
              Back
            </button>
            <button type="button" onClick={toggleTheme} className="theme-toggle !bg-white/15 !text-white hover:!bg-white/25" aria-label="Toggle theme">
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </AuthGradientBackground>
      </div>

      <div className="relative z-10 -mt-10 flex min-w-0 flex-1 flex-col rounded-t-[2rem] bg-white px-5 pb-10 pt-8 shadow-[0_-8px_40px_rgba(15,23,42,0.06)] ring-1 ring-black/[0.03] dark:bg-surface-950 dark:shadow-[0_-12px_48px_rgba(0,0,0,0.55)] dark:ring-white/[0.06] md:mt-0 md:justify-center md:rounded-none md:bg-surface-100 md:px-10 md:pb-12 md:pt-10 md:shadow-none md:ring-0 md:dark:bg-surface-950 safe-bottom">
        <div className="mb-6 hidden items-center justify-between md:flex">
          <Logo size="sm" to="/" />
          <button type="button" onClick={toggleTheme} className="theme-toggle" aria-label="Toggle theme">
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-start md:max-w-md md:justify-center">{formCard}</div>
      </div>
    </div>
  );
};

export default RegisterPage;
